/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module find-and-replace/findandreplace
 */
import { Plugin } from 'ckeditor5/src/core.js';
import FindAndReplaceUI from './findandreplaceui.js';
import FindAndReplaceEditing from './findandreplaceediting.js';
import { CkeditorConstants } from "../../../src/utils/ckeditorconstants";
const {
  helperAttributes: { DATA_FIND_RESULT },
  htmlElement : { SPAN },
  htmlAttributes : { CLASS }
} = CkeditorConstants;
/**
 * The find and replace plugin.
 *
 * For a detailed overview, check the {@glink features/find-and-replace Find and replace feature documentation}.
 *
 * This is a "glue" plugin which loads the following plugins:
 *
 * * The {@link module:find-and-replace/findandreplaceediting~FindAndReplaceEditing find and replace editing feature},
 * * The {@link module:find-and-replace/findandreplaceui~FindAndReplaceUI find and replace UI feature}
 */
export default class FindAndReplace extends Plugin {
    /**
     * @inheritDoc
     */
    static get requires() {
        return [FindAndReplaceEditing, FindAndReplaceUI];
    }
    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'FindAndReplace';
    }
    /**
     * @inheritDoc
     */
    init() {
        const ui = this.editor.plugins.get('FindAndReplaceUI');
        const findAndReplaceEditing = this.editor.plugins.get('FindAndReplaceEditing');
        const state = findAndReplaceEditing.state;
        ui.on('findNext', (event, data) => {
            // Data is contained only for the "find" button.
            if (data) {
                state.searchText = data.searchText;
                findAndReplaceEditing.find(data.searchText, data);
            }
            else {
                // Find next arrow button press.
                this.editor.execute('findNext');
            }
        });
        ui.on('findPrevious', (event, data) => {
            if (data && state.searchText !== data.searchText) {
                findAndReplaceEditing.find(data.searchText);
            }
            else {
                // Subsequent calls.
                this.editor.execute('findPrevious');
            }
        });
        ui.on('replace', (event, data) => {
            if (state.searchText !== data.searchText) {
                findAndReplaceEditing.find(data.searchText);
            }
            const highlightedResult = state.highlightedResult;
            if (highlightedResult) {
                 /**
                 * On Right Hand Panel, certain elements are just static titles/placeholders and dynamic badges,
                 * those elements don't have to be replaced, restricting that here...
                 */
                let restrictToReplaceRHP;
                [...highlightedResult.marker.getRange().getItems()].forEach((val) => {
                  const notReplaceableElement = this.checkItemIsStaticLabel(val);
                  restrictToReplaceRHP = notReplaceableElement.length;
                });
                !restrictToReplaceRHP &&
                  this.editor.execute("replace", data.replaceText, highlightedResult);
            }
        });
        ui.on('replaceAll', (event, data) => {
            // The state hadn't been yet built for this search text.
            if (state.searchText !== data.searchText) {
                findAndReplaceEditing.find(data.searchText);
            }
              /**
               * On Right Hand Panel, certain elements are just titles and dynamic badges,
               * those elements don't have to be replaced, restricting that here...
               */
              let notReplaceableElement;
              state.results._items
                .slice()
                .reverse()
                .forEach((elem) => {
                  [...elem.marker.getRange().getItems()].forEach((val) => {
                    notReplaceableElement = this.checkItemIsStaticLabel(val);
                    if (notReplaceableElement.length) return;
                  });
                  if (notReplaceableElement.length) {
                    state.results.remove(elem.id);
                  }
                });
            this.editor.execute('replaceAll', data.replaceText, state.results);
        });
        // Reset the state when the user invalidated last search results, for instance,
        // by starting typing another search query or changing options.
        ui.on('searchReseted', () => {
            state.clear(this.editor.model);
            findAndReplaceEditing.stop();
            this.removeNoMarkerFindHighlightAttr();
        });
    }

    
  checkItemIsStaticLabel(element) {
    return element
      .getAncestors()
      .reverse()
      .filter((item) =>
        item
          ?.getAttribute(CkeditorConstants.htmlAttributes.CLASS)
          ?.includes(CkeditorConstants.STATIC_LABEL)
      );
  }

  /**
   * findResultHighlighted is not having any marker but showing highlighted because 
   * SPAN element is still present, Here removing those Span.
   */
  removeNoMarkerFindHighlightAttr() {
    this.editor.model.change((writer) => {
      const ItemList = this.getALLHighlightedResutlts();
      ItemList && ItemList.forEach((item) => {
        if (!this.editor.model.markers.has(`findResultHighlighted:${item.getAttribute(DATA_FIND_RESULT)}`)) {
          writer.unwrap(item);
        }
      });
    });
  }

  getALLHighlightedResutlts() {
    const range = this.editor.model.createRangeIn(
      this.editor.model.document.getRoot()
    );
    return [...range.getItems()].filter(
      (item) =>
        item?.name === SPAN &&
        item?.hasAttribute(DATA_FIND_RESULT)
    );
  }
}
